import { useDispatch, useSelector } from "react-redux";
import { getErrorMessage } from "../../../utils/errorUtils";
import logger from "../../../utils/logger";
import { useCallback } from "react";
import { automationRuleManager } from "../../../firebase/Automations";

//#region Initial State

const initialState = {
	// Rule list state
	rules: [],
	isLoading: false,
	error: null,

	// Selected rule for detail view
	selectedRule: null,
	isLoadingRule: false,

	// Templates
	templates: [],
	isLoadingTemplates: false,

	// Analytics
	analytics: null,
	isLoadingAnalytics: false
};

//#endregion
//#region automationsReducer

/**
 * Reducer for the automations state slice.
 * Manages automation rule CRUD, templates, and analytics data.
 */
const automationsReducer = (state = initialState, action) => {
	switch (action.type) {
		// Rule list
		case 'AUTOMATIONS_LOADING':
			return { ...state, isLoading: true, error: null };
		case 'AUTOMATIONS_LOADED':
			return { ...state, isLoading: false, rules: action.payload, error: null };
		case 'AUTOMATIONS_ERROR':
			return { ...state, isLoading: false, error: action.payload };

		// Selected rule
		case 'AUTOMATION_RULE_LOADING':
			return { ...state, isLoadingRule: true };
		case 'AUTOMATION_RULE_LOADED':
			return { ...state, isLoadingRule: false, selectedRule: action.payload };
		case 'AUTOMATION_RULE_CLEARED':
			return { ...state, selectedRule: null };

		// Templates
		case 'AUTOMATION_TEMPLATES_LOADING':
			return { ...state, isLoadingTemplates: true };
		case 'AUTOMATION_TEMPLATES_LOADED':
			return { ...state, isLoadingTemplates: false, templates: action.payload };

		// Analytics
		case 'AUTOMATION_ANALYTICS_LOADING':
			return { ...state, isLoadingAnalytics: true };
		case 'AUTOMATION_ANALYTICS_LOADED':
			return { ...state, isLoadingAnalytics: false, analytics: action.payload };

		// Reset
		case 'AUTOMATIONS_RESET':
			return { ...initialState };

		default:
			return state;
	}
};

//#endregion
//#region ruleCrudActions

/**
 * Load automation rules with optional filters.
 * Calls the automationRuleManager Cloud Function with 'list' action.
 */
export const loadAutomationRules = (queryParameters = {}) => {
	return async (dispatch) => {
		try {
			dispatch({ type: 'AUTOMATIONS_LOADING' });
			const result = await automationRuleManager('list', { queryParameters });
			dispatch({ type: 'AUTOMATIONS_LOADED', payload: result.rules || result || [] });
			return result;
		} catch (error) {
			dispatch({ type: 'AUTOMATIONS_ERROR', payload: getErrorMessage(error) });
			logger.logReduxErrorEvent(error, getErrorMessage(error), true);
			return null;
		}
	};
};

/**
 * Load a single automation rule by ID.
 */
export const loadAutomationRule = (ruleId) => {
	return async (dispatch) => {
		try {
			dispatch({ type: 'AUTOMATION_RULE_LOADING' });
			const result = await automationRuleManager('get', { ruleId });
			dispatch({ type: 'AUTOMATION_RULE_LOADED', payload: result });
			return result;
		} catch (error) {
			logger.logReduxErrorEvent(error, getErrorMessage(error), true);
			return null;
		}
	};
};

/**
 * Create a new automation rule.
 */
export const createAutomationRule = (ruleData) => {
	return async (dispatch) => {
		try {
			const result = await automationRuleManager('create', { ruleData });
			return result;
		} catch (error) {
			logger.logReduxErrorEvent(error, getErrorMessage(error), true);
			return null;
		}
	};
};

/**
 * Update an existing automation rule.
 */
export const updateAutomationRule = (ruleId, ruleData) => {
	return async (dispatch) => {
		try {
			const result = await automationRuleManager('update', { ruleId, ruleData });
			return result;
		} catch (error) {
			logger.logReduxErrorEvent(error, getErrorMessage(error), true);
			return null;
		}
	};
};

/**
 * Delete an automation rule (soft delete).
 */
export const deleteAutomationRule = (ruleId) => {
	return async (dispatch) => {
		try {
			const result = await automationRuleManager('delete', { ruleId });
			return result;
		} catch (error) {
			logger.logReduxErrorEvent(error, getErrorMessage(error), true);
			return null;
		}
	};
};

//#endregion
//#region ruleOperationActions

/**
 * Toggle a rule between ACTIVE and PAUSED status.
 */
export const toggleRuleStatus = (ruleId, currentStatus) => {
	return async (dispatch) => {
		try {
			const action = currentStatus === 'ACTIVE' ? 'pause' : 'activate';
			const result = await automationRuleManager(action, { ruleId });
			return result;
		} catch (error) {
			logger.logReduxErrorEvent(error, getErrorMessage(error), true);
			return null;
		}
	};
};

/**
 * Clone an existing automation rule.
 */
export const cloneRule = (ruleId, ruleData = {}) => {
	return async (dispatch) => {
		try {
			const result = await automationRuleManager('clone', { ruleId, ruleData });
			return result;
		} catch (error) {
			logger.logReduxErrorEvent(error, getErrorMessage(error), true);
			return null;
		}
	};
};

/**
 * Test an automation rule with a sample entity.
 */
export const testRule = (ruleId) => {
	return async (dispatch) => {
		try {
			const result = await automationRuleManager('test', { ruleId });
			return result;
		} catch (error) {
			logger.logReduxErrorEvent(error, getErrorMessage(error), true);
			return null;
		}
	};
};

//#endregion
//#region templateActions

/**
 * Load available automation rule templates.
 */
export const loadTemplates = () => {
	return async (dispatch) => {
		try {
			dispatch({ type: 'AUTOMATION_TEMPLATES_LOADING' });
			const result = await automationRuleManager('getTemplates', {});
			dispatch({ type: 'AUTOMATION_TEMPLATES_LOADED', payload: result.templates || result || [] });
			return result;
		} catch (error) {
			logger.logReduxErrorEvent(error, getErrorMessage(error), true);
			return null;
		}
	};
};

/**
 * Create a new rule from a pre-built template.
 */
export const createFromTemplate = (templateKey, ruleData = {}) => {
	return async (dispatch) => {
		try {
			const result = await automationRuleManager('createFromTemplate', { templateKey, ruleData });
			return result;
		} catch (error) {
			logger.logReduxErrorEvent(error, getErrorMessage(error), true);
			return null;
		}
	};
};

//#endregion
//#region analyticsActions

/**
 * Load automation rule analytics for specified rules and date range.
 */
export const loadAnalytics = (queryParameters = {}) => {
	return async (dispatch) => {
		try {
			dispatch({ type: 'AUTOMATION_ANALYTICS_LOADING' });
			const result = await automationRuleManager('getAnalytics', { queryParameters });
			dispatch({ type: 'AUTOMATION_ANALYTICS_LOADED', payload: result });
			return result;
		} catch (error) {
			logger.logReduxErrorEvent(error, getErrorMessage(error), true);
			return null;
		}
	};
};

/**
 * Clear selected rule from state.
 */
export const clearSelectedRule = () => {
	return (dispatch) => {
		dispatch({ type: 'AUTOMATION_RULE_CLEARED' });
	};
};

/**
 * Reset automations state.
 */
export const resetAutomations = () => {
	return (dispatch) => {
		dispatch({ type: 'AUTOMATIONS_RESET' });
	};
};

//#endregion
//#region useAutomations

/**
 * useAutomations - Custom hook for accessing automation state and actions.
 * Exposes all state values and dispatches all action creators.
 */
export const useAutomations = () => {
	const dispatch = useDispatch();

	const rules = useSelector((appState) => appState.automationsState.rules);
	const isLoading = useSelector((appState) => appState.automationsState.isLoading);
	const error = useSelector((appState) => appState.automationsState.error);
	const selectedRule = useSelector((appState) => appState.automationsState.selectedRule);
	const isLoadingRule = useSelector((appState) => appState.automationsState.isLoadingRule);
	const templates = useSelector((appState) => appState.automationsState.templates);
	const isLoadingTemplates = useSelector((appState) => appState.automationsState.isLoadingTemplates);
	const analytics = useSelector((appState) => appState.automationsState.analytics);
	const isLoadingAnalytics = useSelector((appState) => appState.automationsState.isLoadingAnalytics);

	return {
		AutomationsState: useSelector((appState) => appState.automationsState),

		// Rule list
		rules,
		isLoading,
		error,

		// Selected rule
		selectedRule,
		isLoadingRule,

		// Templates
		templates,
		isLoadingTemplates,

		// Analytics
		analytics,
		isLoadingAnalytics,

		// Rule CRUD actions
		loadAutomationRules: useCallback((filters) => dispatch(loadAutomationRules(filters)), [dispatch]),
		loadAutomationRule: useCallback((ruleId) => dispatch(loadAutomationRule(ruleId)), [dispatch]),
		createAutomationRule: useCallback((ruleData) => dispatch(createAutomationRule(ruleData)), [dispatch]),
		updateAutomationRule: useCallback((ruleId, ruleData) => dispatch(updateAutomationRule(ruleId, ruleData)), [dispatch]),
		deleteAutomationRule: useCallback((ruleId) => dispatch(deleteAutomationRule(ruleId)), [dispatch]),

		// Rule operations
		toggleRuleStatus: useCallback((ruleId, currentStatus) => dispatch(toggleRuleStatus(ruleId, currentStatus)), [dispatch]),
		cloneRule: useCallback((ruleId, ruleData) => dispatch(cloneRule(ruleId, ruleData)), [dispatch]),
		testRule: useCallback((ruleId) => dispatch(testRule(ruleId)), [dispatch]),

		// Templates
		loadTemplates: useCallback(() => dispatch(loadTemplates()), [dispatch]),
		createFromTemplate: useCallback((templateKey, ruleData) => dispatch(createFromTemplate(templateKey, ruleData)), [dispatch]),

		// Analytics
		loadAnalytics: useCallback((params) => dispatch(loadAnalytics(params)), [dispatch]),

		// Cleanup
		clearSelectedRule: useCallback(() => dispatch(clearSelectedRule()), [dispatch]),
		resetAutomations: useCallback(() => dispatch(resetAutomations()), [dispatch])
	};
};

//#endregion

export default automationsReducer;
